import React from "react";
import { Button } from "antd";
import { Card, Badge } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "./planDetails.css";

const CheckCircle = ({ className }: { className?: string }) => {
  return (
    <div className={className}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <circle
          cx="10"
          cy="10"
          r="9"
          fill="#2E605A"
          stroke="#2E605A"
          stroke-width="2"
        />
        <rect
          x="5"
          y="9.82812"
          width="2"
          height="6"
          transform="rotate(-45 5 9.82812)"
          fill="white"
        />
        <rect
          x="13.4844"
          y="7"
          width="2"
          height="8"
          transform="rotate(45 13.4844 7)"
          fill="white"
        />
      </svg>
    </div>
  );
};
// 类型定义
interface Plan {
  name: string;
  price: string;
  period: string;
  description: string;
  tag: string;
}

interface Feature {
  feature: string;
  explorer: boolean | string;
  individual: boolean | string;
  business: boolean | string;
  highlight?: boolean;
}

type FeatureValue = boolean | string;

interface PricingTableProps {
  limitCount?: number;
  limitCountLoading?: boolean;
}

const PricingTable: React.FC<PricingTableProps> = () => {
  // 定价计划数据
  const plans: Plan[] = [
    {
      name: "Business",
      price: "$35",
      period: "/mo",
      description:
        "Growing teams that need collaboration and smarter workflows.",
      tag: "per company",
    },
    {
      name: "Individual",
      price: "$8",
      period: "/mo",
      description: "Individual loan professionals seeking efficiency.",
      tag: "per user",
    },
    {
      name: "Explorer",
      price: "Free",
      period: "",
      description: "Individuals testing the platform.",
      tag: "" // "lifetime",
    },
  ];

  // 功能列表数据
  const features: Feature[] = [
    {
      feature: "Pricing Engine (PPE)",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Digital 1003 (POS)",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Processing System (LOS)",
      explorer: true,
      individual: true,
      business: true,
    },
    { feature: "CRM", explorer: true, individual: true, business: true },
    {
      feature: "AI Qualified Income Calculator",
      explorer: true,
      individual: true,
      business: true,
      highlight: true,
    },
    {
      feature: "AI Document Review",
      explorer: true,
      individual: true,
      business: true,
      highlight: true,
    },
    {
      feature: "Automated Condition Collection",
      explorer: true,
      individual: true,
      business: true,
      highlight: true,
    },
    {
      feature: "Personal Site & Widgets",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Private Lending & Hard Money",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Credit Reporting",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Pre-Qualification Letters",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Mortgage Call Reports",
      explorer: true,
      individual: true,
      business: true,
    },
    {
      feature: "Strata AI",
      explorer: "Limited (10 per day)",
      individual: "Unlimited",
      business: "Unlimited",
      highlight: true,
    },
    {
      feature: "FNM / 1003 Exports",
      explorer: "Limited (10 lifetime)",
      individual: "Unlimited",
      business: "Unlimited",
    },
    {
      feature: "OpenHouse Finder",
      explorer: false,
      individual: true,
      business: true,
    },
    // {
    //   feature: "HomeOwner Marketplace",
    //   explorer: false,
    //   individual: true,
    //   business: true,
    // },
    {
      feature: "Rate Marketplace",
      explorer: false,
      individual: true,
      business: true,
    },
    {
      feature: "Add Seats",
      explorer: false,
      individual: false,
      business: true,
    },
    {
      feature: "Onboarding & Technical Support",
      explorer: false,
      individual: false,
      business: true,
    },
    {
      feature: "Dedicated AE",
      explorer: false,
      individual: false,
      business: true,
    },
    {
      feature: "Dedicated Support",
      explorer: false,
      individual: false,
      business: true,
    },
    {
      feature: "LOS integration",
      explorer: false,
      individual: false,
      business: true,
    },
  ];

  const renderFeatureValue = (value: FeatureValue): React.ReactNode => {
    if (value === true) {
      return <CheckCircle className="check-icon" />;
    }
    if (value === false) {
      return null;
    }
    return <span className="limited-text">{value}</span>;
  };

  return (
    <div className="pricing-container">
      <div className="pricing-content">
        {/* 主标题 */}
        <h1 className="main-title">Plan details</h1>

        {/* 定价卡片 */}
        <div className="pricing-cards">
          {plans.map((plan: Plan, index: number) => (
            <div key={index} className="pricing-card">
              <div className="card-content">
                <h2 className="plan-name">{plan.name}</h2>
                <div className="price-section">
                  <span className="price">{plan.price}</span>
                  {plan.period && <span className="period">{plan.period}</span>}
                </div>
                {plan.tag && <div className="plan-tag">{plan.tag}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* 理想用户说明 */}
        <div className="ideal-for-section">
          <div className="ideal-for-border">
            <div className="section-label">Ideal for</div>
            <div className="ideal-descriptions">
              {plans.map((plan: Plan, index: number) => (
                <div key={index} className="ideal-description-item">
                  <span className="ideal-description">{plan.description}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 功能对比表 */}
        <div className="features-section">
          <h3 className="section-title">Features</h3>
          <div className="features-table-container">
            {/* 表头 */}
            {/* <div className="table-header">
              <div className="header-cell header-feature">Features</div>
              <div className="header-cell header-plan">Explorer</div>
              <div className="header-cell header-plan">Individual</div>
              <div className="header-cell header-plan">Business</div>
            </div> */}

            {/* 功能行 */}
            {features.map((item: Feature, index: number) => (
              <div key={index} className="table-row">
                <div className="feature-cell">
                  <span
                    className={`feature-name ${
                      item.highlight ? "highlight-feature" : ""
                    }`}
                  >
                    {item.feature}
                  </span>
                </div>
                <div className="value-cell">
                  {renderFeatureValue(item.explorer)}
                </div>
                <div className="value-cell">
                  {renderFeatureValue(item.individual)}{item.feature === 'Personal Site & Widgets' && <span className="whitelabeling">+ Whitelabeling</span>}
                </div>
                <div className="value-cell">
                  {renderFeatureValue(item.business)}{item.feature === 'Personal Site & Widgets' && <span className="whitelabeling">+ Whitelabeling</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 企业级计划 */}
        <div className="enterprise-plans">
          <div className="enterprise-card">
            <div className="enterprise-content">
              <div className="enterprise-left">
                <div className="enterprise-header">
                  <div className="enterprise-label">Enterprise</div>
                  <div className="enterprise-title">Talk to us</div>
                </div>
                <div className="enterprise-features">
                  <CheckCircle className="check-icon" />
                  <span className="enterprise-feature-text">
                    Everything in Business, plus customization.
                  </span>
                </div>
              </div>
              <div className="enterprise-right">
                <button className="schedule-btn" onClick={() => {
                  window.open('https://www.zeitro.com/demo', '_blank')
                }}>Schedule demo</button>
              </div>
            </div>
          </div>

          <div className="enterprise-card">
            <div className="enterprise-content">
              <div className="enterprise-left">
                <div className="enterprise-header">
                  <div className="enterprise-label">Loan Product Listing</div>
                  <div className="enterprise-title">Talk to us</div>
                </div>
                <p className="enterprise-description">
                  AI-powered product listings, live in seconds and marketed to
                  loan officers nationwide.
                </p>
                <div className="enterprise-features">
                  <CheckCircle className="check-icon" />
                  <span className="enterprise-feature-text">
                    Ratesheet, Guidelines, Loan File & Condition sync
                    integrations.
                  </span>
                </div>
              </div>
              <div className="enterprise-right">
                <button className="schedule-btn" onClick={() => {
                  window.open('https://www.zeitro.com/demo', '_blank')
                }}>Schedule demo</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const App: React.FC<PricingTableProps> = (props) => {
  return <PricingTable {...props} />;
};

export default App;
